<template>
  <div class="hints-manager">
    <div class="hints-manager__header">
      <div>
        <h1>Podpowiedzi dla użytkowników</h1>
        <p>
          Zarządzaj chmurkami z podpowiedziami widocznymi na wybranych podstronach serwisu.
          Każda podpowiedź może zawierać wiele slajdów.
        </p>
      </div>
    </div>

    <div v-if="isLoading" class="state-box">
      Ładowanie podpowiedzi...
    </div>

    <div v-else-if="!editingHint" class="hints-manager__list">
      <div class="hints-search">
        <label for="hint-page-name-search">Szukaj po nazwie strony</label>
        <input
            id="hint-page-name-search"
            v-model.trim="pageNameSearch"
            type="text"
            placeholder="Wpisz nazwę strony"
        >
      </div>

      <div v-if="!hints.length" class="state-box">
        Brak dodanych podpowiedzi.
      </div>

      <div v-else-if="!filteredHints.length" class="state-box">
        Nie znaleziono podpowiedzi dla podanej nazwy strony.
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
          <tr>
            <th>Nazwa strony</th>
            <th>Status</th>
            <th>Liczba slajdów</th>
            <th>Akcje</th>
          </tr>
          </thead>

          <tbody>
          <tr v-for="hint in filteredHints" :key="hint.id">
            <td>
              {{ hint.page_name }}
            </td>
            <td>
                <span
                    class="status"
                    :class="hint.is_active ? 'status--active' : 'status--inactive'"
                >
                  {{ hint.is_active ? 'Aktywna' : 'Nieaktywna' }}
                </span>
            </td>
            <td>{{ getStepsCount(hint) }}</td>
            <td>
              <div class="actions">
                <button type="button" class="link-button" @click="editHint(hint)">
                  Edytuj
                </button>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <form v-else class="hint-form" @submit.prevent="saveHint">
      <div class="hint-form__top">
        <h2>{{ editingHint.id ? 'Edycja podpowiedzi' : 'Nowa podpowiedź' }}</h2>

        <div class="hint-form__actions">
          <button type="button" class="btn btn--secondary" @click="cancelEdit">
            Anuluj
          </button>
          <button type="submit" class="btn btn--primary" :disabled="isSaving">
            {{ isSaving ? 'Zapisywanie...' : 'Zapisz podpowiedź' }}
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert--error">
        {{ errorMessage }}
      </div>

      <section class="card">
        <h3>Dane podpowiedzi</h3>

        <div class="form-grid">
          <div class="form-field">
            <label for="page_key">Klucz strony *</label>
            <input
                id="page_key"
                v-model.trim="editingHint.page_key"
                type="text"
                required
                placeholder="np. business-profile-calendar"
                readonly
            >
            <small>Unikalny identyfikator miejsca, dla którego ma być wyświetlana podpowiedź. Nie możesz go zmienić.</small>
          </div>

          <div class="form-field">
            <input
                id="target_element"
                v-model.trim="editingHint.target_element"
                type="hidden">
          </div>
        </div>

        <label class="checkbox-field">
          <input v-model="editingHint.is_active" type="checkbox">
          <span>Podpowiedź aktywna</span>
        </label>
      </section>

      <section class="card">
        <div class="steps-header">
          <div>
            <h3>Slajdy podpowiedzi</h3>
            <p>Dodaj treści, które użytkownik zobaczy kolejno w chmurce z podpowiedzią.</p>
          </div>

          <button type="button" class="btn btn--success" @click="addStep">
            Dodaj slajd
          </button>
        </div>

        <div v-if="!editingHint.steps.length" class="state-box">
          Ta podpowiedź nie ma jeszcze slajdów. Dodaj pierwszy slajd.
        </div>

        <div
            v-for="(step, index) in editingHint.steps"
            :key="step.localId"
            class="step-card"
        >
          <div class="step-card__header">
            <h4>Slajd {{ index + 1 }}</h4>

            <div class="step-card__actions">
              <button
                  type="button"
                  class="small-button"
                  :disabled="index === 0"
                  @click="moveStep(index, -1)"
              >
                W górę
              </button>
              <button
                  type="button"
                  class="small-button"
                  :disabled="index === editingHint.steps.length - 1"
                  @click="moveStep(index, 1)"
              >
                W dół
              </button>
              <button
                  type="button"
                  class="small-button small-button--danger"
                  @click="removeStep(index)"
              >
                Usuń
              </button>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-field">
              <label :for="`step-title-${index}`">Tytuł slajdu *</label>
              <input
                  :id="`step-title-${index}`"
                  v-model.trim="step.title"
                  type="text"
                  required
                  placeholder="Wpisz tytuł slajdu"
              >
            </div>

            <div class="form-field">
              <label :for="`step-order-${index}`">Kolejność *</label>
              <input
                  :id="`step-order-${index}`"
                  v-model.number="step.order"
                  type="number"
                  min="0"
                  required
              >
            </div>
          </div>

          <div class="form-field">
            <label>Zdjęcie slajdu</label>

            <div v-if="step.image_path" class="image-preview">
              <img :src="getImageUrl(step.image_path)" alt="Zdjęcie slajdu">
              <button type="button" class="link-button link-button--danger" @click="removeStepImage(index)">
                Usuń zdjęcie
              </button>
            </div>

            <input
                type="file"
                accept="image/*"
                @change="handleFileUpload($event, index)"
            >
            <small>Możesz wgrać jedno zdjęcie dla slajdu.</small>
          </div>

          <div class="form-field">
            <label>Treść slajdu *</label>
            <CKEditor
                :model-value="step.content"
                @update:model-value="step.content = $event"
            />
          </div>
        </div>
      </section>

      <div class="hint-form__bottom">
        <button type="button" class="btn btn--secondary" @click="cancelEdit">
          Anuluj
        </button>
        <button type="submit" class="btn btn--primary" :disabled="isSaving">
          {{ isSaving ? 'Zapisywanie...' : 'Zapisz podpowiedź' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import hintService from '@/services/hint.service';
import CKEditor from '@/components/CKEditor.vue';
import axios from "axios";

const hints = ref([]);
const pageNameSearch = ref('');
const editingHint = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const errorMessage = ref('');

const filteredHints = computed(() => {
  const searchPhrase = pageNameSearch.value.toLowerCase();

  if (!searchPhrase) {
    return hints.value;
  }

  return hints.value.filter((hint) => (
      hint.page_name || ''
  ).toLowerCase().includes(searchPhrase));
});

const createLocalId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const normalizeStep = (step, index) => ({
  id: step.id,
  title: step.title || '',
  content: step.content || '',
  order: Number.isInteger(step.order) ? step.order : index,
  image_path: step.image_path || null,
  localId: createLocalId(),
});

const normalizeHint = (hint) => ({
  id: hint.id,
  page_key: hint.page_key || '',
  target_element: hint.target_element || '',
  is_active: Boolean(hint.is_active),
  steps: Array.isArray(hint.steps)
      ? hint.steps
          .slice()
          .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
          .map(normalizeStep)
      : [],
});

const getStepsCount = (hint) => {
  if (!Array.isArray(hint.steps)) {
    return 0;
  }

  return hint.steps.length;
};

const refreshStepsOrder = () => {
  editingHint.value.steps = editingHint.value.steps.map((step, index) => ({
    ...step,
    order: index,
  }));
};

const fetchHints = async () => {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await hintService.getAll();
    hints.value = Array.isArray(response.data) ? response.data : [];
  } catch (err) {
    errorMessage.value = 'Nie udało się pobrać listy podpowiedzi.';
  } finally {
    isLoading.value = false;
  }
};

const editHint = async (hint) => {
  errorMessage.value = '';

  try {
    const response = await hintService.getById(hint.id);
    editingHint.value = normalizeHint(response.data);
  } catch (err) {
    editingHint.value = normalizeHint(hint);
  }
};

const cancelEdit = () => {
  editingHint.value = null;
  errorMessage.value = '';
};

const addStep = () => {
  editingHint.value.steps.push(normalizeStep({
    title: '',
    content: '',
    order: editingHint.value.steps.length,
    image_path: null,
  }, editingHint.value.steps.length));
};

const removeStep = (index) => {
  if (!confirm('Czy na pewno chcesz usunąć ten slajd?')) {
    return;
  }

  editingHint.value.steps.splice(index, 1);
  refreshStepsOrder();
};

const moveStep = (index, direction) => {
  const newIndex = index + direction;

  if (newIndex < 0 || newIndex >= editingHint.value.steps.length) {
    return;
  }

  const movedStep = editingHint.value.steps.splice(index, 1)[0];
  editingHint.value.steps.splice(newIndex, 0, movedStep);
  refreshStepsOrder();
};

const removeStepImage = (stepIndex) => {
  editingHint.value.steps[stepIndex].image_path = null;
};

const handleFileUpload = async (event, stepIndex) => {
  const file = event.target.files[0];

  if (!file) {
    return;
  }

  const formData = new FormData();
  formData.append('image', file);

  try {
    const response = await axios.post(`/UploadHintStepImage/${editingHint.value.id}/${editingHint.value.steps[stepIndex].id}`, formData);
    editingHint.value.steps[stepIndex].image_path = response.data.image_path;
  } catch (err) {
    alert('Nie udało się wgrać zdjęcia.');
  } finally {
    event.target.value = '';
  }
};

const validateHint = () => {
  if (!editingHint.value.page_key) {
    return 'Pole „Klucz strony” jest wymagane.';
  }

  if (!Array.isArray(editingHint.value.steps)) {
    return 'Lista slajdów jest nieprawidłowa.';
  }

  for (let i = 0; i < editingHint.value.steps.length; i++) {
    const step = editingHint.value.steps[i];

    if (!step.title) {
      return `Tytuł slajdu ${i + 1} jest wymagany.`;
    }

    if (!step.content) {
      return `Treść slajdu ${i + 1} jest wymagana.`;
    }

    if (!Number.isInteger(Number(step.order))) {
      return `Kolejność slajdu ${i + 1} musi być liczbą całkowitą.`;
    }
  }

  return '';
};

const buildPayload = () => ({
  id: editingHint.value.id,
  page_key: editingHint.value.page_key,
  target_element: editingHint.value.target_element || null,
  is_active: Boolean(editingHint.value.is_active),
  steps: editingHint.value.steps.map((step, index) => ({
    id: step.id,
    title: step.title,
    content: step.content,
    order: Number.isInteger(Number(step.order)) ? Number(step.order) : index,
    image_path: step.image_path || null,
  })),
});

const saveHint = async () => {
  errorMessage.value = '';
  refreshStepsOrder();

  const validationMessage = validateHint();

  if (validationMessage) {
    errorMessage.value = validationMessage;
    return;
  }

  isSaving.value = true;

  try {
    await hintService.save(buildPayload());
    editingHint.value = null;
    await fetchHints();
  } catch (err) {
    errorMessage.value = err.response?.data?.message || 'Nie udało się zapisać podpowiedzi.';
  } finally {
    isSaving.value = false;
  }
};

const getImageUrl = (path) => {
  if (!path) {
    return '';
  }

  if (path.startsWith('http')) {
    return path;
  }

  return `${process.env.VUE_APP_STORAGE_URL || ''}${path}`;
};

onMounted(fetchHints);
</script>

<style scoped lang="scss">
.hints-manager {
  padding: 32px;
  color: #1f2937;

  &__header,
  .hint-form__top,
  .hint-form__bottom,
  .steps-header,
  .step-card__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  &__header {
    margin-bottom: 24px;

    h1 {
      margin: 0 0 8px;
      font-size: 2.8rem;
      color: #176b4d;
    }

    p {
      margin: 0;
      max-width: 760px;
      font-size: 1.5rem;
      line-height: 1.5;
      color: #6b7280;
    }
  }
}

.hints-search {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 420px;
  margin-bottom: 18px;

  label {
    font-size: 1.4rem;
    font-weight: 700;
    color: #374151;
  }

  input {
    min-height: 42px;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    font-size: 1.4rem;
  }

  input:focus {
    border-color: #10b981;
    outline: none;
  }
}

.table-wrapper {
  overflow-x: auto;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 1.4rem;

  th,
  td {
    padding: 14px 16px;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #f9fafb;
    color: #374151;
    font-weight: 700;
  }

  tr:last-child td {
    border-bottom: 0;
  }
}

.card,
.step-card,
.state-box,
.alert {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
}

.card {
  padding: 24px;
  margin-bottom: 24px;

  h3 {
    margin: 0 0 18px;
    font-size: 2rem;
    color: #176b4d;
  }
}

.state-box,
.alert {
  padding: 18px;
  font-size: 1.5rem;
}

.alert--error {
  margin-bottom: 18px;
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.hint-form {
  &__top {
    margin-bottom: 18px;

    h2 {
      margin: 0;
      font-size: 2.4rem;
      color: #176b4d;
    }
  }

  &__actions,
  &__bottom {
    display: flex;
    gap: 12px;
  }

  &__bottom {
    justify-content: flex-end;
  }
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;

  label {
    font-size: 1.4rem;
    font-weight: 700;
    color: #374151;
  }

  input {
    min-height: 42px;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    font-size: 1.4rem;
  }

  small {
    font-size: 1.2rem;
    color: #6b7280;
  }
}

.checkbox-field {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 1.4rem;
  font-weight: 700;
}

.steps-header {
  margin-bottom: 18px;

  h3 {
    margin-bottom: 6px;
  }

  p {
    margin: 0;
    font-size: 1.4rem;
    color: #6b7280;
  }
}

.step-card {
  padding: 20px;
  margin-bottom: 18px;
  background: #f9fafb;

  h4 {
    margin: 0 0 18px;
    font-size: 1.8rem;
    color: #111827;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.image-preview {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 10px;

  img {
    max-width: 180px;
    max-height: 120px;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    object-fit: cover;
  }
}

.status {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 1.2rem;
  font-weight: 700;

  &--active {
    background: #dcfce7;
    color: #15803d;
  }

  &--inactive {
    background: #fee2e2;
    color: #b91c1c;
  }
}

.actions {
  display: flex;
  gap: 12px;
}

.btn,
.small-button,
.link-button {
  cursor: pointer;
  border: 0;
  font-weight: 700;
  transition: opacity 0.2s ease, background-color 0.2s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
}

.btn {
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 1.4rem;

  &--primary {
    background: #176b4d;
    color: #ffffff;
  }

  &--secondary {
    background: #e5e7eb;
    color: #1f2937;
  }

  &--success {
    background: #10b981;
    color: #ffffff;
  }
}

.small-button {
  padding: 7px 10px;
  border-radius: 8px;
  background: #e5e7eb;
  color: #1f2937;

  &--danger {
    background: #fee2e2;
    color: #b91c1c;
  }
}

.link-button {
  padding: 0;
  background: transparent;
  color: #176b4d;
  font-size: 1.4rem;
  text-decoration: underline;

  &--danger {
    color: #dc2626;
  }
}

:deep(.ck-editor__editable) {
  min-height: 220px;
}

@media (max-width: 900px) {
  .hints-manager {
    padding: 20px;

    &__header,
    .hint-form__top,
    .hint-form__bottom,
    .steps-header,
    .step-card__header {
      flex-direction: column;
    }
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .hint-form__actions,
  .hint-form__bottom {
    width: 100%;
    flex-direction: column;

    .btn {
      width: 100%;
    }
  }
}
</style>